import { Button } from "@/components/ui/button";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { BeforeAfterSlider } from "./BeforeAfterSlider";
import { useIOSPortrait } from "@/hooks/useIOSPortrait";

const highlights = [
  "100% Authentic products only",
  "Curated for real skin concerns",
  "Skincare education & guidance",
  "Trusted by customers",
];

export function AboutPreview() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isIOSPortrait = useIOSPortrait();

  // iOS Portrait: Render completely static version to prevent Framer Motion transforms
  if (isIOSPortrait) {
    return (
      <section ref={ref} className="section-padding bg-card">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image Side - Static */}
            <div className="relative">
              <BeforeAfterSlider />
            </div>

            {/* Content Side - Static */}
            <div>
              <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary mb-4">
                About Us
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6 leading-tight">
                Your Trusted
                <br />
                <span className="text-gradient">Skincare Partner</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                At Nurture Skin Studio, we believe in the power of{" "}
                <Link to="/products" className="text-primary hover:underline">
                  authentic skincare products
                </Link>. 
                We carefully curate products from trusted global brands, ensuring every 
                item meets our high standards for quality and effectiveness.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Based in Lekki, Lagos, we're not just a skincare store—we're your partners 
                in achieving healthy, radiant skin. Read{" "}
                <Link to="/testimonials" className="text-primary hover:underline">
                  what our customers say
                </Link>{" "}
                or{" "}
                <Link to="/contact" className="text-primary hover:underline">
                  get personalized skincare advice
                </Link>{" "}
                from our experts.
              </p>

              {/* Highlights - Static */}
              <ul className="space-y-3 mb-10">
                {highlights.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{item}</span>
                  </li>
                ))}
              </ul>

              <Button variant="default" size="lg" asChild>
                <Link to="/about" className="inline-flex items-center gap-2">
                  Learn More About Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="section-padding bg-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <BeforeAfterSlider />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary mb-4">
              About Us
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6 leading-tight">
              Your Trusted
              <br />
              <span className="text-gradient">Skincare Partner</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              At Nurture Skin Studio, we believe in the power of{" "}
              <Link to="/products" className="text-primary hover:underline">
                authentic skincare products
              </Link>. 
              We carefully curate products from trusted global brands, ensuring every 
              item meets our high standards for quality and effectiveness.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Based in Lekki, Lagos, we're not just a skincare store—we're your partners 
              in achieving healthy, radiant skin. Read{" "}
              <Link to="/testimonials" className="text-primary hover:underline">
                what our customers say
              </Link>{" "}
              or{" "}
              <Link to="/contact" className="text-primary hover:underline">
                get personalized skincare advice
              </Link>{" "}
              from our experts.
            </p>

            {/* Highlights */}
            <ul className="space-y-3 mb-10">
              {highlights.map((item, index) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">{item}</span>
                </motion.li>
              ))}
            </ul>

            <Button variant="default" size="lg" asChild>
              <Link to="/about" className="inline-flex items-center gap-2">
                Learn More About Us
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
